import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';

import { i18nReady } from './i18n';
import './styles/global.css';
// Marketing-only styling, scoped under .landing so it never reaches the signed-in app.
import './styles/landing.css';
import './styles/blog.css';
import './styles/consent.css';
// The public calculator, also scoped under .landing — it renders in the marketing shell.
import './styles/calculator.css';
// The signed-in shell, scoped under .shell for the same reason.
import './styles/shell.css';
import './styles/appbar.css';
// The signed-out pages, scoped under .auth.
import './styles/auth.css';
// Two components substantial enough to own their stylesheet rather than grow global.css further.
import './styles/calendar.css';
import './styles/charts.css';
import './styles/walkthrough.css';
import './styles/compliance.css';
import './styles/invoices.css';
import './styles/datefield.css';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { reportClientError } from './lib/reportError';
import { queryClient } from './lib/queryClient';
import { watchForStaleBuild } from './lib/staleBuild';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found.');
}

// Before anything can navigate: a tab left open across a deploy recovers by itself.
watchForStaleBuild();

// The boundary below renders the fallback; these hooks are what makes the crash visible to us.
// React 19 routes every error through them, including the ones a boundary swallows and the ones it
// recovers from on a retry — a boundary alone would leave both invisible.
const root = createRoot(container, {
  onUncaughtError: (error, info) => reportClientError(error, 'uncaught', info.componentStack),
  onCaughtError: (error, info) => reportClientError(error, 'boundary', info.componentStack),
  onRecoverableError: (error, info) => reportClientError(error, 'recoverable', info.componentStack),
});

/* Waits for the detected locale's bundle. Bulgarian is bundled, so this resolves in a microtask for
   most visitors; an English one pays a single request rather than watching the page repaint out of
   Bulgarian. A failure still renders — an untranslated app beats a blank one. */
void i18nReady.catch(() => undefined).then(() =>
  root.render(
  <StrictMode>
    {/* Outside the providers, so a crash inside React Query or the router still lands here. */}
    <ErrorBoundary showHomeLink>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
  ),
);
